/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('variant:changed', function (event) {
  var productContainer = event.target.closest('product-rerender');
  var saleBadge = productContainer ? productContainer.querySelector('[data-sale-badge]') : null;
  var variant = event.detail.variant;

  if (!saleBadge) {
    return;
  }

  saleBadge.hidden = !variant || !variant.compare_at_price || variant.compare_at_price <= variant.price;
});

class ContactFormLayout extends HTMLElement {
  connectedCallback() {
    this.shopifyFormStyleAttempts = 0;
    this.applyShopifyFormEmbedTweaks();
  }

  disconnectedCallback() {
    window.clearTimeout(this.shopifyFormStyleTimer);

    if (this.shopifyFormObserver) {
      this.shopifyFormObserver.disconnect();
      this.shopifyFormObserver = null;
    }
  }

  applyShopifyFormEmbedTweaks() {
    const formEmbed = this.querySelector('shopify-forms-embed');
    const shadowRoot = formEmbed && formEmbed.shadowRoot;

    if (shadowRoot) {
      if (!shadowRoot.getElementById('contact-form-embed-tweaks')) {
        const style = document.createElement('style');
        style.id = 'contact-form-embed-tweaks';
        style.textContent = `
          @media screen and (max-width: 490px) {
            [data-testid="app-router"] {
              grid-template-columns: minmax(0, 1fr) !important;
            }

            #form-container-ref {
              width: 100% !important;
            }
          }

          [id$="-selector-label"] {
            border: 0 !important;
            clip: rect(0, 0, 0, 0) !important;
            height: 1px !important;
            margin: -1px !important;
            overflow: hidden !important;
            padding: 0 !important;
            position: absolute !important;
            white-space: nowrap !important;
            width: 1px !important;
          }

          [id$="-selector-value"]:empty::before {
            color: var(--form-placeholder-color, rgba(32, 32, 32, 0.5));
            content: attr(data-placeholder);
          }
        `;
        shadowRoot.appendChild(style);
      }

      this.applyShopifyFormDropdownPlaceholders(shadowRoot);

      if (!this.shopifyFormObserver) {
        this.shopifyFormObserver = new MutationObserver(() => {
          this.applyShopifyFormDropdownPlaceholders(shadowRoot);
        });
        this.shopifyFormObserver.observe(shadowRoot, { childList: true, subtree: true });
      }

      return;
    }

    this.shopifyFormStyleAttempts += 1;

    if (this.shopifyFormStyleAttempts < 100) {
      this.shopifyFormStyleTimer = window.setTimeout(
        () => this.applyShopifyFormEmbedTweaks(),
        100
      );
    }
  }

  applyShopifyFormDropdownPlaceholders(shadowRoot) {
    shadowRoot.querySelectorAll('[id$="-selector-label"]').forEach((label) => {
      const value = label.parentElement && label.parentElement.querySelector('[id$="-selector-value"]');
      const placeholder = label.textContent.replace(/\s+/g, ' ').trim();

      if (value && placeholder && value.getAttribute('data-placeholder') !== placeholder) {
        value.setAttribute('data-placeholder', placeholder);
      }
    });
  }
}

if (!customElements.get('contact-form-layout')) {
  customElements.define('contact-form-layout', ContactFormLayout);
}

document.addEventListener('variant:changed', function(event) {
  const root = document.querySelector('[data-product-identifiers]');
  if (!root) {
    return;
  }

  const variant = event.detail && event.detail.variant;
  const map = getProductIdentifiersMap(root);
  const fromMap = variant && map && map.variants ? map.variants[String(variant.id)] : null;

  const upc = String((fromMap && fromMap.barcode) || (variant && variant.barcode) || '').trim();
  const item = String((fromMap && fromMap.item) || '').trim();
  const sku = String((fromMap && fromMap.sku) || '').trim();

  updateProductIdentifier(root, 'upc', upc);
  updateProductIdentifier(root, 'item', item);
  updateProductIdentifier(root, 'sku', sku);

  root.hidden = !(upc || item || sku);
});

/**
 * Prefer the map next to the identifiers block (supports multiple products on page).
 * @param {HTMLElement} root
 * @returns {{ variants?: Record<string, { barcode?: string, item?: string, sku?: string }> } | null}
 */
function getProductIdentifiersMap(root) {
  const scoped =
    root.parentElement && root.parentElement.querySelector('[data-product-identifiers-map]');
  const mapEl = scoped || document.querySelector('[data-product-identifiers-map]');
  if (!mapEl) {
    return null;
  }

  try {
    return JSON.parse(mapEl.textContent);
  } catch (_) {
    return null;
  }
}

/**
 * @param {HTMLElement} root
 * @param {'upc' | 'item' | 'sku'} key
 * @param {string} value
 */
function updateProductIdentifier(root, key, value) {
  const block = root.querySelector('[data-product-' + key + '-block]');
  const labelEl = root.querySelector('[data-product-' + key + '-label]');
  const valueEl = root.querySelector('[data-product-' + key + '-value]');
  if (!block) {
    return;
  }

  if (!value) {
    block.hidden = true;
    if (labelEl) {
      labelEl.textContent = '';
    }
    if (valueEl) {
      valueEl.textContent = '';
    }
    return;
  }

  if (labelEl) {
    labelEl.textContent = root.getAttribute('data-label-' + key) || '';
  }
  if (valueEl) {
    valueEl.textContent = value;
  }
  block.hidden = false;
}

function closeCartTooltips(exceptTrigger) {
  document.querySelectorAll('[data-cart-tooltip].is-open').forEach(function(el) {
    if (exceptTrigger && el === exceptTrigger) {
      return;
    }
    el.classList.remove('is-open');
    el.setAttribute('aria-expanded', 'false');
    const panelId = el.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (panel) {
      panel.hidden = true;
    }
  });
}

document.addEventListener('click', function(event) {
  const cartTrigger = event.target.closest('[data-cart-tooltip]');
  const guaranteeTrigger = event.target.closest('[data-guarantee-tooltip]');
  const openGuaranteeTriggers = document.querySelectorAll('[data-guarantee-tooltip].is-open');

  if (cartTrigger) {
    event.preventDefault();
    const wasOpen = cartTrigger.classList.contains('is-open');
    closeCartTooltips();
    openGuaranteeTriggers.forEach(function(el) {
      el.classList.remove('is-open');
    });

    if (!wasOpen) {
      cartTrigger.classList.add('is-open');
      cartTrigger.setAttribute('aria-expanded', 'true');
      const panelId = cartTrigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) {
        panel.hidden = false;
      }
    }
    return;
  }

  if (guaranteeTrigger) {
    closeCartTooltips();
    const wasOpen = guaranteeTrigger.classList.contains('is-open');
    openGuaranteeTriggers.forEach(function(el) {
      el.classList.remove('is-open');
    });
    if (!wasOpen) {
      guaranteeTrigger.classList.add('is-open');
    }
    return;
  }

  closeCartTooltips();
  openGuaranteeTriggers.forEach(function(el) {
    el.classList.remove('is-open');
  });
});

document.addEventListener('keydown', function(event) {
  if (event.key === 'Escape') {
    closeCartTooltips();
  }
});


/**
 * Cart Take 10 + coupon helpers (OOTB Cart AJAX discounts + take10_rewards attribute).
 */
(function() {
  const cartRoot = () => window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';

  function cartUpdateUrl() {
    return cartRoot() + 'cart/update.js';
  }

  function getMiniCartSectionId() {
    const drawer = document.getElementById('mini-cart');
    return (drawer && drawer.getAttribute('section')) || 'mini-cart';
  }

  function refreshCartUi(cart) {
    const detail = {};

    if (cart) {
      detail.cart = cart;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: detail
      })
    );

    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
    }
  }

  async function updateCart(payload) {
    const body = Object.assign({}, payload, {
      sections: [getMiniCartSectionId()]
    });

    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(function() {
      return null;
    });

    if (!response.ok) {
      const message =
        (data && (data.description || data.message)) || 'Unable to update cart.';
      throw new Error(message);
    }

    return data;
  }

  function setTake10Loading(toggle, isLoading) {
    const root = toggle.closest('[data-cart-take10]');
    const loader = root && root.querySelector('.cart-take10__loader');

    toggle.disabled = isLoading;
    if (root) {
      root.classList.toggle('is-loading', isLoading);
    }
    if (loader) {
      loader.hidden = !isLoading;
    }
  }


  function setCouponStatus(root, message, isError) {
    const status = root.querySelector('[data-cart-coupon-status]');
    if (!status) {
      return;
    }

    if (!message) {
      status.hidden = true;
      status.textContent = '';
      status.classList.remove('is-error', 'is-success');
      return;
    }

    status.hidden = false;
    status.textContent = message;
    status.classList.toggle('is-error', Boolean(isError));
    status.classList.toggle('is-success', !isError);
  }

  document.addEventListener('change', async function(event) {
    const toggle = event.target.closest('[data-cart-take10-toggle]');
    if (!toggle || toggle.disabled) {
      return;
    }

    const enable = toggle.checked;
    const take10Root = toggle.closest('[data-cart-take10]');
    const appliedCoupon =
      (take10Root && take10Root.getAttribute('data-applied-coupon-code')) || '';

    // Take10 and coupon codes are mutually exclusive. The toggle is already rendered
    // disabled in that state, so this only guards a cart that changed underneath us.
    if (enable && appliedCoupon) {
      toggle.checked = false;
      const conflict = take10Root.querySelector('[data-cart-take10-conflict]');
      if (conflict) {
        conflict.hidden = false;
      }
      return;
    }

    setTake10Loading(toggle, true);

    try {
      const cart = await updateCart({
        attributes: {
          take10_rewards: enable ? '1' : ''
        }
      });
      refreshCartUi(cart);
    } catch (error) {
      toggle.checked = !enable;
      console.error(error);
      setTake10Loading(toggle, false);
    }
  });


  document.addEventListener('input', function(event) {
    const input = event.target.closest('[data-cart-coupon] input[name="discount"]');
    if (input) {
      setCouponStatus(input.closest('[data-cart-coupon]'), '');
    }
  });


  document.addEventListener('submit', async function(event) {
    const form = event.target.closest('[data-cart-coupon-form]');
    if (!form) {
      return;
    }

    event.preventDefault();

    const root = form.closest('[data-cart-coupon]');
    const input = form.querySelector('input[name="discount"]');
    const code = input ? String(input.value || '').trim() : '';
    const fallbackError =
      (root && root.getAttribute('data-coupon-error')) || 'Enter a valid coupon code';

    if (!code) {
      setCouponStatus(root, fallbackError, true);
      return;
    }

    // Take10 and coupon codes are mutually exclusive: refuse the code rather than
    // silently dropping the reward the customer already redeemed.
    if (root && root.getAttribute('data-take10-applied') === 'true') {
      const conflictTemplate =
        root.getAttribute('data-coupon-conflict') ||
        '“%code%” cannot be combined with take10 Rewards';

      setCouponStatus(root, conflictTemplate.replace('%code%', code), true);
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
    }

    try {
      const cart = await updateCart({ discount: code });

      const codes = (cart && cart.discount_codes) || [];
      const normalized = code.toLowerCase();
      const applicable = codes.some(function(entry) {
        return entry && entry.applicable && String(entry.code || '').toLowerCase() === normalized;
      });

      if (!applicable) {
        await updateCart({ discount: '' });
        setCouponStatus(root, fallbackError, true);
        return;
      }

      refreshCartUi(cart);
    } catch (error) {
      setCouponStatus(root, error.message || fallbackError, true);
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  });



  document.addEventListener('click', async function(event) {
    const removeButton = event.target.closest('[data-cart-coupon-remove]');
    if (!removeButton) {
      return;
    }

    event.preventDefault();
    removeButton.disabled = true;

    try {
      const cart = await updateCart({ discount: '' });
      refreshCartUi(cart);
    } catch (error) {
      console.error(error);
      removeButton.disabled = false;
    }
  });

  // When the last cart item is removed, clear Take10 and force a clean drawer re-render
  // (theme empty-state path can stall if section HTML is missing from the change response).
  document.documentElement.addEventListener('cart:updated', function(event) {
    const cart = event.detail && event.detail.cart;
    if (!cart || cart.item_count !== 0) {
      return;
    }

    updateCart({
      attributes: {
        take10_rewards: ''
      },
      discount: ''
    }).catch(function() {});

    window.setTimeout(function() {
      const drawer = document.getElementById('mini-cart');
      if (!drawer) {
        return;
      }

      const stillHasItemsUi =
        drawer.querySelector('.line-item') || drawer.querySelector('.mini-cart__drawer-footer');

      if (stillHasItemsUi) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: { item_count: 0 }
            }
          })
        );
      }
    }, 700);
  });
})();

document.addEventListener('click', function(event) {
  const lessButton = event.target.closest('.facet-more-values__less');
  if (!lessButton) {
    return;
  }

  const details = lessButton.closest('.facet-more-values');
  if (!details) {
    return;
  }

  details.open = false;

  const summary = details.querySelector('.facet-more-values__summary');
  if (summary) {
    summary.focus();
  }
});

(function () {
  function getAddOnCheckbox(form) {
    if (!form) {
      return null;
    }

    if (form.querySelector) {
      const nestedCheckbox = form.querySelector('[data-product-add-on]');
      if (nestedCheckbox) {
        return nestedCheckbox;
      }
    }

    const formId = form.getAttribute('id');
    if (formId) {
      const associatedCheckbox = document.querySelector('[data-product-add-on][form="' + formId + '"]');
      if (associatedCheckbox) {
        return associatedCheckbox;
      }
    }

    return Array.from(form.elements || []).find(function (element) {
      return element.hasAttribute && element.hasAttribute('data-product-add-on');
    }) || null;
  }

  function getFormId(checkbox) {
    const owner = checkbox.form;

    return (owner && owner.getAttribute('id')) || checkbox.getAttribute('form') || '';
  }

  function refreshCart(sections, openMiniCart) {
    return fetch(`${window.themeVariables.routes.cartUrl}.js`)
      .then(function (response) {
        return response.json();
      })
      .then(function (cartContent) {
        if (sections) {
          cartContent.sections = sections;
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: openMiniCart
          }
        }));
      });
  }

  function addOnVariant(variantId, sourceProductId) {
    const payload = {
      id: variantId,
      quantity: 1,
      sections: ['mini-cart']
    };

    if (sourceProductId) {
      payload.properties = {
        _source_product_id: sourceProductId
      };
    }

    return fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (json) {
        return {
          ok: response.ok,
          json: json
        };
      });
    });
  }

  // Quick View re-renders its whole subtree on a variant change, so the checkbox the shopper
  // ticked is replaced by a fresh unchecked one. Keep the selection keyed by form id and
  // restore it once the replacement is in the DOM.
  const selectedAddOns = new Map();

  document.addEventListener('change', function (event) {
    const checkbox = event.target.closest && event.target.closest('[data-product-add-on]');
    if (!checkbox) {
      return;
    }

    const formId = getFormId(checkbox);
    if (!formId) {
      return;
    }

    if (checkbox.checked) {
      selectedAddOns.set(formId, checkbox.value);
    } else {
      selectedAddOns.delete(formId);
    }
  });

  function restoreAddOnSelection(formId) {
    if (!selectedAddOns.has(formId)) {
      return;
    }

    const checkbox = getAddOnCheckbox(document.getElementById(formId));
    if (checkbox && checkbox.value === selectedAddOns.get(formId)) {
      checkbox.checked = true;
    }
  }

  document.addEventListener('variant:changed', function (event) {
    const formId = event.target.getAttribute && event.target.getAttribute('id');
    if (!formId || !selectedAddOns.has(formId)) {
      return;
    }

    // The replacement usually lands in the same task, so restore in a microtask to avoid a
    // frame where the box looks unticked, then again on the next frame as a safety net.
    Promise.resolve().then(function () {
      restoreAddOnSelection(formId);
    });
    requestAnimationFrame(function () {
      restoreAddOnSelection(formId);
    });
  });

  // On the cart page, and whenever the cart is a full page, the theme redirects to /cart as soon
  // as the main product is added and never dispatches `variant:added`. The add-on therefore has
  // to be sent before that submit is allowed to run.
  function addLeavesPage() {
    const settings = window.themeVariables.settings;

    return settings.cartType === 'page' || settings.pageType === 'cart';
  }

  document.addEventListener('submit', function (event) {
    const form = event.target;

    if (!addLeavesPage() || !form.matches || !form.matches('form[is="product-form"]')) {
      return;
    }

    // The re-submit below carries this flag so the theme handles it as usual.
    if (form.hasAttribute('data-add-on-sent')) {
      form.removeAttribute('data-add-on-sent');
      return;
    }

    const checkbox = getAddOnCheckbox(form);
    if (!checkbox || !checkbox.checked) {
      return;
    }

    const variantId = Number(checkbox.value);
    if (!variantId) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter;
    const sourceProductId = checkbox.getAttribute('data-source-product-id');

    addOnVariant(variantId, sourceProductId).catch(function () {}).then(function () {
      checkbox.checked = false;
      selectedAddOns.delete(getFormId(checkbox));
      form.setAttribute('data-add-on-sent', '');

      if (form.requestSubmit) {
        form.requestSubmit(submitter);
      } else {
        form.submit();
      }
    });
  }, true);

  document.addEventListener('variant:added', function (event) {
    const form = event.target;
    const checkbox = getAddOnCheckbox(form);

    if (!checkbox || !checkbox.checked) {
      return;
    }

    const variantId = Number(checkbox.value);
    if (!variantId) {
      return;
    }

    const sourceProductId = checkbox.getAttribute('data-source-product-id');

    addOnVariant(variantId, sourceProductId).then(function (result) {
      if (!result.ok) {
        return;
      }

      checkbox.checked = false;
      selectedAddOns.delete(getFormId(checkbox));

      return refreshCart(
        result.json.sections,
        window.themeVariables.settings.cartType === 'drawer' && form.closest('.drawer') === null
      );
    }).catch(function () {});
  });
})();

/**
 * While a cart line is updating, lock every quantity/remove control so
 * rapid clicks cannot fire a second /cart/change.js with a stale line number.
 */
(function () {
  const ACTION_SELECTOR = 'line-item-quantity a, line-item-quantity input, line-item-quantity button';

  function cartRoot(fromElement) {
    return fromElement.closest('.cart, cart-drawer') || document;
  }

  function lockCartActions(fromElement) {
    const root = cartRoot(fromElement);

    root.classList.add('cart-is-updating');
    root.querySelectorAll(ACTION_SELECTOR).forEach(function (control) {
      control.setAttribute('aria-disabled', 'true');

      if (control.tagName === 'INPUT' || control.tagName === 'BUTTON') {
        control.disabled = true;
      }
    });
  }

  function unlockCartActions(fromElement) {
    const root = cartRoot(fromElement);

    root.classList.remove('cart-is-updating');
    root.querySelectorAll(ACTION_SELECTOR).forEach(function (control) {
      control.removeAttribute('aria-disabled');

      if (control.tagName === 'INPUT' || control.tagName === 'BUTTON') {
        control.disabled = false;
      }
    });
  }

  document.addEventListener('line-item-quantity:change:start', function (event) {
    lockCartActions(event.target);
  });

  document.addEventListener('line-item-quantity:change:end', function (event) {
    unlockCartActions(event.target);
  });

  document.addEventListener('click', function (event) {
    const control = event.target.closest(ACTION_SELECTOR);

    if (!control) {
      return;
    }

    const root = cartRoot(control);

    if (root.classList.contains('cart-is-updating')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);
})();


/**
 * SalonBiz booking widget (sections/salonbiz-widget.liquid).
 *
 * SalonBiz serves a separate embed URL per device, so mount only the one that
 * matches the viewport. The embeds live in <template> tags rather than being
 * hidden with CSS because a display:none iframe still boots the booking app.
 */
class SalonbizWidget extends HTMLElement {
  connectedCallback() {
    this.mount = this.querySelector('[data-salonbiz-mount]');

    if (!this.mount) {
      return;
    }

    const breakpoint = parseInt(this.getAttribute('mobile-breakpoint'), 10) || 980;

    this.mediaQuery = window.matchMedia('(max-width: ' + (breakpoint - 1) + 'px)');
    this.onBreakpointChange = this.render.bind(this);

    this.render();
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);
  }

  disconnectedCallback() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
    }
  }

  render() {
    const wanted = this.mediaQuery.matches ? 'mobile' : 'desktop';
    const template =
      this.querySelector('template[data-salonbiz="' + wanted + '"]') ||
      this.querySelector('template[data-salonbiz]');

    // Re-mounting the same embed would reload the iframe and drop the booking session.
    if (!template || this.mountedTemplate === template) {
      return;
    }

    this.mount.replaceChildren(template.content.cloneNode(true));
    this.mountedTemplate = template;
  }
}

if (!customElements.get('salonbiz-widget')) {
  customElements.define('salonbiz-widget', SalonbizWidget);
}
